import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import axios from "axios";
import config from "../config/config";
import {
  connectClaimSocket,
  disconnectClaimSocket,
} from "../websockets/ClaimSocketService";
import { useUser } from "./UserContext";
import { useAdminUser } from "./AdminUserContext";

const ClaimContext = createContext();

export const ClaimProvider = ({ children }) => {
  const { clientUser, workerUser } = useUser();
  const { adminUser } = useAdminUser();
  const [claimsData, setClaimsData] = useState([]);
  const [invoicesData, setInvoicesData] = useState([]);
  const [triggerFetch, setTriggerFetch] = useState(false);

  const fetchClaimsByUserId = async (userId) => {
    try {
      const response = await axios.get(
        `${config.API_BASE_URL}/v1/claims/user/${userId}`
      );
      const claimsWithRates = response.data.map((claim) => ({
        ...claim,
      }));

      const sortedClaims = claimsWithRates.sort(
        (a, b) =>
          new Date(b.claimSubmissionDate) - new Date(a.claimSubmissionDate)
      );

      setClaimsData(sortedClaims);
    } catch (error) {
      console.error("Failed to fetch claims by user ID", error);
    }
  };

  const fetchClaimsByClientId = async (clientId) => {
    try {
      const response = await axios.get(
        `${config.API_BASE_URL}/v1/claims/client/${clientId}`
      );
      const claimsWithRates = response.data.map((claim) => ({
        ...claim,
      }));

      const sortedClaims = claimsWithRates.sort(
        (a, b) =>
          new Date(b.claimSubmissionDate) - new Date(a.claimSubmissionDate)
      );

      setClaimsData(sortedClaims);
    } catch (error) {
      console.error("Failed to fetch claims by client ID", error);
    }
  };

  const fetchClaimById = async (claimId) => {
    try {
      const response = await axios.get(
        `${config.API_BASE_URL}/v1/claims/${claimId}`
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching claim:", error);
      return null;
    }
  };

  const fetchAllClaims = useCallback(async () => {
    try {
      const response = await axios.get(`${config.API_BASE_URL}/v1/claims`);
      const claimsWithRates = response.data.map((claim) => ({
        ...claim,
      }));
      const sortedClaims = claimsWithRates.sort(
        (a, b) =>
          new Date(b.claimSubmissionDate) - new Date(a.claimSubmissionDate)
      );
      setClaimsData(sortedClaims);
    } catch (error) {
      console.error("Failed to fetch claims", error);
    }
  }, []);

  const fetchInvoices = useCallback(async () => {
    try {
      const response = await axios.get(`${config.API_BASE_URL}/v1/invoices`);
      const invoices = response.data.sort(
        (a, b) => new Date(b.created * 1000) - new Date(a.created * 1000)
      );
      setInvoicesData(invoices);
    } catch (error) {
      console.error("Failed to fetch invoices", error);
    }
  }, []);

  const fetchTimeLeftByClaimId = async (claimId) => {
    try {
      const response = await axios.get(
        `${config.API_BASE_URL}/v1/claims/time-left/${claimId}`
      );
      return response.data;
    } catch (error) {
      console.error("Failed to fetch time left for claim", error);
      return null;
    }
  };

  useEffect(() => {
    const user = clientUser || workerUser || adminUser;
    if (user && user.id) {
      const fetchData = () => {
        if (adminUser) {
          fetchAllClaims();
        } else if (clientUser) {
          fetchClaimsByClientId(user.id);
        } else if (workerUser) {
          fetchClaimsByUserId(user.id);
        }
      };

      fetchData();

      connectClaimSocket(setClaimsData, setTriggerFetch);
      return () => {
        disconnectClaimSocket();
      };
    }
  }, [clientUser, workerUser, adminUser]);

  return (
    <ClaimContext.Provider
      value={{
        claimsData,
        invoicesData,
        setClaimsData,
        fetchClaimsByUserId,
        fetchClaimsByClientId,
        fetchClaimById,
        fetchAllClaims,
        fetchInvoices,
        fetchTimeLeftByClaimId,
        triggerFetch,
      }}
    >
      {children}
    </ClaimContext.Provider>
  );
};

export const useClaim = () => useContext(ClaimContext);
